import React, { useCallback, useEffect, useMemo, useState, useContext } from 'react';
import _ from 'lodash';
import { GridCellParams } from '@mui/x-data-grid-pro';
import { Button, Stack, Typography, Box, Card, CircularProgress } from '@mui/material';
import { QuickSearch } from 'components/_atoms';
import { translate as t } from 'hooks/useTranslations';
import useUrlParams from 'hooks/useUrlParams';
import { AppContext } from 'views/App/context';
import useDTBPackages from 'views/App/Portal/DTB/Packages/_data_source/useDTBPackages';
import AssignmentFilterModal from '../_components/FilterModal';
import GridSection from 'components/_layouts/GridSection';
import PageToolbar from '_lex/_atoms/PageToolbar';
import CommonLayout from 'views/App/Portal/_layouts/CommonLayout';
import PackageAssignmentRowActions from 'views/App/Portal/DTB/_components/PackageAssignmentRowActions';
import type { DTBPackage } from 'views/App/Portal/DTB/_types/dtb';
import { FilterAlt } from '@mui/icons-material';

const defaultParams = {
  orgId: '',
  jurisdictionId: undefined,
  manualId: undefined,
  createdAtStart: undefined,
  createdAtEnd: undefined,
  search: undefined,
};

const AssignmentList: React.FC = () => {
  const { currentOrganizationId } = useContext(AppContext);
  const [params, setParams] = useUrlParams(_.omit(defaultParams, ['orgId']));
  const [packages, setPackages] = useState<DTBPackage[]>([]);
  const [filterModalOpen, setFilterModalOpen] = useState(false);

  const isFiltered = useMemo(() => !_.isEmpty(_.omitBy(params, _.isEmpty)), [params]);

  const clearParams = useCallback(() => {
    setParams({ ..._.omit(defaultParams, ['orgId']) });
    document.body.dispatchEvent(new Event('params-changed'));
  }, [setParams]);

  const queryParams = useMemo(() => {
    const cleanParams = _.omitBy({ ...params }, _.isEmpty);
    return {
      orgId: currentOrganizationId,
      status: 'APPROVED',
      jurisdictionId: cleanParams.jurisdictionId,
      manualId: cleanParams.manualId,
      createdAtStart: cleanParams.createdAtStart,
      createdAtEnd: cleanParams.createdAtEnd,
      search: cleanParams.search,
    };
  }, [currentOrganizationId, params]);

  const packagesQuery = useDTBPackages(queryParams);

  useEffect(() => {
    if (packagesQuery.data && !packagesQuery.isError) {
      const fetchedPackages =
        packagesQuery.data.pages.flatMap((page: { content: DTBPackage[] }) => page.content) || [];
      setPackages(fetchedPackages);
    }
  }, [packagesQuery.data, packagesQuery.isError]);

  const updateParams = useCallback(
    (updatedParams: any) => {
      setParams((prev: any) => ({
        ...prev,
        ...updatedParams,
      }));
      document.body.dispatchEvent(new Event('params-changed'));
    },
    [setParams]
  );

  const onSearchChange = useCallback(
    (val: string) => {
      updateParams({ search: val });
    },
    [updateParams]
  );

  const columns = useMemo(
    () => ({
      name: {
        header: t('dtbs.assignments.grid.packageName', 'Package Name'),
        flex: 2,
        renderer: (params: GridCellParams) => <Typography variant='h6'>{params.row.name}</Typography>,
      },
      manual: {
        header: t('dtbs.assignments.grid.manual', 'Manual'),
        flex: 1,
        renderer: (params: GridCellParams) => <Typography>{params.row.manual?.name || '-'}</Typography>,
      },

      jurisdiction: {
        header: t('dtbs.library.table.jurisdiction', 'Jurisdiction'),
        flex: 1,
        renderer: (params: GridCellParams) => <Typography>{params.row.jurisdiction?.name}</Typography>,
      },

      createdAt: {
        header: t('dtbs.assignments.grid.created', 'Created'),
        flex: 1,
        renderer: (params: GridCellParams) => (
          <Typography>{params.row.createdAt ? params.row.createdAt.split('T')[0] : '-'}</Typography>
        ),
      },
      actions: {
        header: '',
        type: 'actions',
        minWidth: 200,
        flex: 0.5,
        columnProps: {
          pinned: 'right',
          hideable: false,
          sortable: false,
          resizable: false,
        },
        renderer: (params: GridCellParams) => (
          <PackageAssignmentRowActions row={params.row as DTBPackage} />
        ),
      },
    }),
    [t]
  );

  return (
    <CommonLayout
      headerProps={{
        title: t('dtbs.assignments.title', 'DTB Package Assignment'),
        subtitle: '',
      }}
    >
      <Box sx={{ display: 'grid', gridTemplateRows: 'auto 1fr', gap: 4, minHeight: 0 }}>
        <PageToolbar
          leftItems={
            <Stack
              direction='row'
              spacing={2}
              sx={{ alignItems: 'center', flex: 1 }}
            >
              <QuickSearch
                id='assignment_search'
                initialValue={params.search || ''}
                onChange={onSearchChange}
                placeholder={t('dtbs.assignments.filters.search', 'Search packages, manual, jurisdiction')}
              />
              <Button
                variant='outlined'
                size='small'
                onClick={() => setFilterModalOpen(true)}
                startIcon={<FilterAlt />}
                className='icon-only'
              />
              {isFiltered && (
                <Button
                  color='neutral'
                  onClick={clearParams}
                  size='small'
                  sx={{
                    whiteSpace: 'nowrap',
                    minWidth: '80px',
                    px: 2,
                  }}
                >
                  {t('dtbs.packages.clear')}
                </Button>
              )}
            </Stack>
          }
        />
        {packagesQuery.isLoading ? (
          <Card sx={{ p: 4, display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
            <CircularProgress />
          </Card>
        ) : packages.length === 0 ? (
          <Card sx={{ p: 4, display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
            <Stack
              spacing={3}
              alignItems='center'
            >
              <Box
                component='img'
                src='/admin/img/policy/dtbs/empty-package-placeholder.png'
                alt='Empty packages'
              />
              <Typography
                variant='subtitleLg'
                color='text.secondary'
              >
                {t('dtbs.packages.emptyStateFiltered', 'No packages match your current filters')}
              </Typography>
            </Stack>
          </Card>
        ) : (
          <GridSection
            columns={columns}
            data={packages}
            query={{
              isLoading: packagesQuery.isFetching,
              isError: false,
              data: { pages: [{ count: packages.length }] },
            }}
          />
        )}
        <AssignmentFilterModal
          open={filterModalOpen}
          onClose={() => setFilterModalOpen(false)}
          filters={params}
          onApply={updateParams}
        />
      </Box>
    </CommonLayout>
  );
};

export default AssignmentList;
